import React, { useState } from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GripVertical, Facebook, Instagram, Twitter, Linkedin, Youtube, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import type { SocialMediaLink, SocialPlatform } from "./types";

const platformIcons = {
  facebook: Facebook,
  instagram: Instagram,
  twitter: Twitter,
  linkedin: Linkedin,
  youtube: Youtube,
  email: Mail,
  map: MapPin,
};

const platformDefaults = {
  facebook: { urlPrefix: 'https://facebook.com/' },
  instagram: { urlPrefix: 'https://instagram.com/' },
  twitter: { urlPrefix: 'https://twitter.com/' },
  linkedin: { urlPrefix: 'https://linkedin.com/in/' },
  youtube: { urlPrefix: 'https://youtube.com/@' },
  email: { urlPrefix: 'mailto:' },
  map: { urlPrefix: '' },
};

interface SocialMediaSettingsProps {
  links: SocialMediaLink[];
  onChange: (links: SocialMediaLink[]) => void;
  isPro: boolean;
}

const SortableItem = ({ link, onLinkChange, onPlatformChange }: { 
  link: SocialMediaLink; 
  onLinkChange: (id: string, updates: Partial<SocialMediaLink>) => void;
  onPlatformChange: (id: string, platform: SocialPlatform) => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: link.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const Icon = platformIcons[link.platform];
  const isEmailOrMap = link.platform === 'email' || link.platform === 'map';
  const placeholder = isEmailOrMap 
    ? link.platform === 'email' 
      ? 'Enter email address'
      : 'Enter map URL'
    : 'username';

  const validateAndUpdateLink = (value: string) => {
    if (link.platform === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        toast.error('Please enter a valid email address');
        return;
      }
    }

    const updates: Partial<SocialMediaLink> = {};
    
    if (isEmailOrMap) {
      updates.url = link.platform === 'email' ? `mailto:${value}` : value;
    } else {
      updates.username = value;
      updates.url = `${platformDefaults[link.platform].urlPrefix}${value}`;
    }
    
    onLinkChange(link.id, updates);
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className="p-3 mb-2 bg-white hover:shadow-sm transition-all duration-200"
    >
      <div className="flex items-center gap-3">
        <div
          {...attributes}
          {...listeners}
          className="cursor-move hover:text-primary"
        >
          <GripVertical className="h-4 w-4" />
        </div>
        
        <div className="flex-1 flex items-center gap-3">
          <Select
            value={link.platform}
            onValueChange={(value: SocialPlatform) => onPlatformChange(link.id, value)}
          >
            <SelectTrigger className="w-[130px]">
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 text-gray-500" />
                <SelectValue placeholder="Select" />
              </div>
            </SelectTrigger>
            <SelectContent>
              {Object.keys(platformIcons).map((platform) => (
                <SelectItem key={platform} value={platform}>
                  <div className="flex items-center gap-2">
                    {React.createElement(platformIcons[platform as SocialPlatform], { 
                      className: "h-4 w-4" 
                    })}
                    <span>{platform.charAt(0).toUpperCase() + platform.slice(1)}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex-1 relative">
            {!isEmailOrMap && (
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">@</span>
            )}
            <Input
              value={isEmailOrMap ? (link.url || '').replace('mailto:', '') : link.username || ''}
              onChange={(e) => validateAndUpdateLink(e.target.value)}
              placeholder={placeholder}
              disabled={!link.enabled}
              className={!isEmailOrMap ? "pl-8" : ""}
            />
          </div>

          <Switch
            checked={link.enabled}
            onCheckedChange={(checked) => onLinkChange(link.id, { enabled: checked })}
          />
        </div>
      </div>
    </Card>
  );
};

export const SocialMediaSettings = ({ links, onChange, isPro }: SocialMediaSettingsProps) => {
  const maxLinks = isPro ? 7 : 3;
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleLinkChange = (id: string, updates: Partial<SocialMediaLink>) => {
    const updatedLinks = links.map((link) =>
      link.id === id ? { ...link, ...updates } : link
    );
    onChange(updatedLinks);
  };

  const handlePlatformChange = (id: string, platform: SocialPlatform) => {
    const updatedLinks = links.map((link) =>
      link.id === id ? { ...link, platform, username: '', url: '' } : link
    );
    onChange(updatedLinks);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const oldIndex = links.findIndex((link) => link.id === active.id);
      const newIndex = links.findIndex((link) => link.id === over.id);
      
      const newLinks = [...links];
      const [movedItem] = newLinks.splice(oldIndex, 1);
      newLinks.splice(newIndex, 0, movedItem);
      
      const updatedLinks = newLinks.map((link, index) => ({
        ...link,
        order: index
      }));
      
      onChange(updatedLinks);
    }
  };

  const addNewLink = () => {
    if (links.length >= maxLinks) {
      toast.error(`Free accounts are limited to ${maxLinks} social links`);
      return;
    }

    onChange([
      ...links,
      {
        id: Math.random().toString(36).substr(2, 9),
        platform: 'facebook',
        enabled: true,
        order: links.length,
      },
    ]);
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-500">
        Configure your social media links and additional contact options. 
        {!isPro && ` Free accounts are limited to ${maxLinks} social links.`}
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={links.map(link => link.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {links
              .sort((a, b) => a.order - b.order)
              .map((link) => (
                <SortableItem
                  key={link.id}
                  link={link}
                  onLinkChange={handleLinkChange}
                  onPlatformChange={handlePlatformChange}
                />
              ))}
          </div>
        </SortableContext>
      </DndContext>

      {links.length < maxLinks && (
        <Button
          variant="outline"
          onClick={addNewLink}
          className="w-full"
        >
          Add Social Link
        </Button>
      )}
    </div>
  );
};